// src/pages/Dashboard.js

import React from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CardActions,
    Button,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Avatar,
    useTheme,
    alpha
} from '@mui/material';
import {
    Message as MessageIcon,
    People as PeopleIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Business as BusinessIcon,
    LocationOn as LocationOnIcon,
    CheckCircle as CheckCircleIcon,
    Pending as PendingIcon,
    Visibility as VisibilityIcon,
    Reply as ReplyIcon,
    Archive as ArchiveIcon,
    TrendingUp as TrendingUpIcon,
    Dashboard as DashboardIcon,
    Assignment as AssignmentIcon,
    Handshake as HandshakeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
    const navigate = useNavigate();
    const theme = useTheme();

    // Module information
    const modules = [
        {
            id: 'contacts',
            title: 'Contact Messages',
            icon: <MessageIcon sx={{ fontSize: 40 }} />,
            description: 'Manage customer inquiries and support requests received through the contact form.',
            features: [
                { icon: <VisibilityIcon />, text: 'View all incoming messages' },
                { icon: <ReplyIcon />, text: 'Reply to customer inquiries' },
                { icon: <ArchiveIcon />, text: 'Archive and organize messages' },
                { icon: <CheckCircleIcon />, text: 'Track message status (Pending/Read/Replied/Archived)' }
            ],
            stats: {
                total: 'View All',
                pending: 'Pending',
                replied: 'Replied'
            },
            path: '/contacts',
            color: '#1976d2',
            lightColor: '#e3f2fd'
        },
        {
            id: 'partnerships',
            title: 'Partnership Requests',
            icon: <HandshakeIcon sx={{ fontSize: 40 }} />,
            description: 'Review and respond to strategic partnership proposals from organizations and institutions.',
            features: [
                { icon: <VisibilityIcon />, text: 'View partnership proposals' },
                { icon: <ReplyIcon />, text: 'Respond to partnership inquiries' },
                { icon: <BusinessIcon />, text: 'Track partner organizations' },
                { icon: <CheckCircleIcon />, text: 'Monitor partnership status' }
            ],
            stats: {
                total: 'View All',
                pending: 'Pending Review',
                replied: 'Responded'
            },
            path: '/partnerships',
            color: '#2e7d32',
            lightColor: '#e8f5e9'
        }
    ];

    const quickStats = [
        {
            title: 'Contact Messages',
            icon: <MessageIcon />,
            description: 'Customer inquiries and support requests',
            color: '#1976d2',
            path: '/contacts'
        },
        {
            title: 'Partnership Requests',
            icon: <HandshakeIcon />,
            description: 'Strategic partnership proposals',
            color: '#2e7d32',
            path: '/partnerships'
        }
    ];

    return (
        <Box>
            {/* Page Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DashboardIcon sx={{ color: '#d32f2f', fontSize: 35 }} />
                    Admin Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    Manage contact messages and partnership requests from your dashboard
                </Typography>
            </Box>

            {/* Quick Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {quickStats.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={6} key={index}>
                        <Card 
                            sx={{ 
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: theme.shadows[8],
                                },
                                borderRadius: 3,
                                borderLeft: `4px solid ${stat.color}`
                            }}
                            onClick={() => navigate(stat.path)}
                        >
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar 
                                        sx={{ 
                                            bgcolor: alpha(stat.color, 0.1), 
                                            color: stat.color,
                                            width: 56,
                                            height: 56
                                        }}
                                    >
                                        {stat.icon}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {stat.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {stat.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                            <CardActions sx={{ px: 2, pb: 2 }}>
                                <Button 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ 
                                        borderColor: stat.color,
                                        color: stat.color,
                                        '&:hover': {
                                            backgroundColor: alpha(stat.color, 0.05),
                                            borderColor: stat.color
                                        }
                                    }}
                                    onClick={() => navigate(stat.path)}
                                >
                                    View {stat.title}
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Module Details */}
            <Grid container spacing={3}>
                {modules.map((module) => (
                    <Grid item xs={12} md={6} key={module.id}>
                        <Paper 
                            sx={{ 
                                p: 3, 
                                borderRadius: 3,
                                height: '100%',
                                borderTop: `4px solid ${module.color}`,
                                transition: 'box-shadow 0.2s',
                                '&:hover': {
                                    boxShadow: theme.shadows[4],
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Avatar 
                                    sx={{ 
                                        bgcolor: module.lightColor,
                                        color: module.color,
                                        width: 56,
                                        height: 56
                                    }}
                                >
                                    {module.icon}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        {module.title}
                                    </Typography>
                                    <Chip 
                                        label="Active" 
                                        size="small" 
                                        color="success"
                                        sx={{ mt: 0.5 }}
                                    />
                                </Box>
                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {module.description}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                                Key Features:
                            </Typography>
                            <List dense disablePadding>
                                {module.features.map((feature, idx) => (
                                    <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            {React.cloneElement(feature.icon, { 
                                                sx: { fontSize: 18, color: module.color } 
                                            })}
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={feature.text}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Status Overview
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                            <Chip 
                                                icon={<PendingIcon />} 
                                                label={module.stats.pending} 
                                                size="small" 
                                                color="warning"
                                                variant="outlined"
                                            />
                                            <Chip 
                                                icon={<CheckCircleIcon />} 
                                                label={module.stats.replied} 
                                                size="small" 
                                                color="success"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Quick Action
                                        </Typography>
                                        <Button 
                                            variant="contained" 
                                            size="small"
                                            sx={{ 
                                                mt: 0.5,
                                                backgroundColor: module.color,
                                                '&:hover': {
                                                    backgroundColor: module.color,
                                                    filter: 'brightness(0.9)'
                                                }
                                            }}
                                            onClick={() => navigate(module.path)}
                                        >
                                            {module.stats.total}
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Quick Actions Section */}
            <Paper sx={{ p: 3, mt: 4, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon sx={{ color: '#d32f2f' }} />
                    Quick Actions
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button 
                            fullWidth 
                            variant="outlined" 
                            startIcon={<MessageIcon />}
                            sx={{ 
                                py: 1.5,
                                borderColor: '#1976d2',
                                color: '#1976d2',
                                '&:hover': {
                                    backgroundColor: alpha('#1976d2', 0.05),
                                    borderColor: '#1976d2'
                                }
                            }}
                            onClick={() => navigate('/contacts')}
                        >
                            View Contact Messages
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button 
                            fullWidth 
                            variant="outlined" 
                            startIcon={<HandshakeIcon />}
                            sx={{ 
                                py: 1.5,
                                borderColor: '#2e7d32',
                                color: '#2e7d32',
                                '&:hover': {
                                    backgroundColor: alpha('#2e7d32', 0.05),
                                    borderColor: '#2e7d32'
                                }
                            }}
                            onClick={() => navigate('/partnerships')}
                        >
                            View Partnership Requests
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button 
                            fullWidth 
                            variant="outlined" 
                            startIcon={<TrendingUpIcon />}
                            sx={{ 
                                py: 1.5,
                                borderColor: '#9c27b0',
                                color: '#9c27b0',
                                '&:hover': {
                                    backgroundColor: alpha('#9c27b0', 0.05),
                                    borderColor: '#9c27b0'
                                }
                            }}
                        >
                            View Analytics
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button 
                            fullWidth 
                            variant="outlined" 
                            startIcon={<PendingIcon />}
                            sx={{ 
                                py: 1.5,
                                borderColor: '#ed6c02',
                                color: '#ed6c02',
                                '&:hover': {
                                    backgroundColor: alpha('#ed6c02', 0.05),
                                    borderColor: '#ed6c02'
                                }
                            }}
                        >
                            Pending Items
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Footer Info */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                    Envsol Technologies Uganda Limited - Admin Dashboard
                </Typography>
            </Box>
        </Box>
    );
};

export default Dashboard;